/**
 * ConfirmDialog - Global Confirmation Modal System
 * 
 * Replaces native window.confirm() which is unreliable in React SPAs.
 * React re-renders can dismiss native dialogs before user interaction.
 *
 * Usage:
 * const { showConfirm } = useConfirm();
 * showConfirm({
 *     title: 'Confirm Delete',
 *     message: 'Delete this item?',
 *     variant: 'danger',        // 'danger' | 'primary' | 'warning'
 *     confirmText: 'Delete',    // optional, defaults based on variant
 *     onConfirm: async () => { ... }
 * });
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Trash, Send, AlertTriangle } from 'lucide-react';
import '../styles/confirm-dialog.css';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within ConfirmProvider');
    }
    return context;
};

export const ConfirmProvider = ({ children }) => {
    const [confirmState, setConfirmState] = useState(null);

    const showConfirm = useCallback((options) => {
        setConfirmState(options);
    }, []);

    const handleCancel = useCallback(() => {
        const cancelAction = confirmState?.onCancel;
        setConfirmState(null);
        if (cancelAction) cancelAction();
    }, [confirmState]);

    const handleConfirm = useCallback(() => {
        if (confirmState?.onConfirm) {
            const action = confirmState.onConfirm;
            setConfirmState(null);
            action();
        }
    }, [confirmState]);

    // Escape key closes
    useEffect(() => {
        if (!confirmState) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') setConfirmState(null);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [confirmState]);

    const getIcon = () => {
        switch (confirmState?.variant) {
            case 'danger': return <Trash size={20} />;
            case 'warning': return <AlertTriangle size={20} />;
            default: return <Send size={20} />;
        }
    };

    const getTitle = () => {
        if (confirmState?.title) return confirmState.title;
        switch (confirmState?.variant) {
            case 'danger': return 'Confirm Delete';
            case 'warning': return 'Are You Sure?';
            default: return 'Confirm Action';
        }
    };

    const getConfirmText = () => {
        if (confirmState?.confirmText) return confirmState.confirmText;
        switch (confirmState?.variant) {
            case 'danger': return 'Delete';
            case 'warning': return 'Continue';
            default: return 'Confirm';
        }
    };

    return (
        <ConfirmContext.Provider value={{ showConfirm }}>
            {children}
            {confirmState && (
                <>
                    <div className="confirm-overlay" onClick={handleCancel} />
                    <div className="confirm-modal">
                        <div className="confirm-header">
                            <div className={`confirm-icon confirm-icon-${confirmState.variant || 'primary'}`}>
                                {getIcon()}
                            </div>
                            <h3 className="confirm-title">{getTitle()}</h3>
                        </div>
                        <p className="confirm-message">{confirmState.message}</p>
                        <div className="confirm-actions">
                            <button className="btn btn-secondary" onClick={handleCancel}>
                                {confirmState.cancelText || 'Cancel'}
                            </button>
                            <button
                                className={`btn confirm-btn-${confirmState.variant || 'primary'}`}
                                onClick={handleConfirm}
                            >
                                {getConfirmText()}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </ConfirmContext.Provider>
    );
};

export default ConfirmProvider;
